import React, { useEffect, useRef, useState } from 'react';
import { ModalRef } from 'components/Modal/Modal';
import ExpandIcon from 'assets/icons/expand.svg?react';
import IconButton from 'components/Button/IconButton';
import ImageWithFallback from 'components/ImageWithFallback/ImageWithFallback';
import RectangleSkeleton from 'components/Skeletons/RectangleSkeleton';
import ExpandedCameraImageModal from '../../../ExpandedCameraImageModal/ExpandedCameraImageModal';
import styles from './CameraImageCard.module.css';

export type CameraImageLabel = {
    content: string;
    position: {
        top?: number;
        left?: number;
        right?: number;
        bottom?: number;
    };
};

export type CarouselImage = {
    src: string;
    fallbackSrc?: string;
    labels?: CameraImageLabel[];
};

type CameraImageCardProps = {
    image: CarouselImage;
    images?: CarouselImage[];
    imageIndex?: number;
    isExpandable?: boolean;
    onLoadingChange?: (isLoading: boolean) => void;
};

export default function CameraImageCard({
    image,
    images = [],
    imageIndex = 0,
    isExpandable = false,
    onLoadingChange,
}: CameraImageCardProps): JSX.Element {
    const [loading, setLoading] = useState(true);
    const [expandedImage, setExpandedImage] = useState<string | null>(null);
    const [expandedFallbackImage, setExpandedFallbackImage] = useState<string | null>(null);
    const [expandedIndex, setExpandedIndex] = useState(imageIndex);
    const imageModalRef = useRef<ModalRef>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
        setLoading(true);
        onLoadingChange?.(true);

        // Handle cached images that may be complete before onLoad fires.
        if (imageRef.current?.complete) {
            setLoading(false);
            onLoadingChange?.(false);
        }
    }, [image.src, onLoadingChange]);

    function finishLoading() {
        setLoading(false);
        onLoadingChange?.(false);
    }

    function handleImageError(event: React.SyntheticEvent<HTMLImageElement>) {
        const failedSrc = event.currentTarget.currentSrc || event.currentTarget.src;
        const fallbackSrc = image.fallbackSrc;
        const hasFallback = !!fallbackSrc && fallbackSrc !== image.src;

        if (!hasFallback || failedSrc === fallbackSrc) {
            finishLoading();
        }
    }

    const shouldShowExpandableIcon = !loading && isExpandable;

    return (
        <div className={styles.imageFrame}>
            <ImageWithFallback
                ref={imageRef}
                src={image.src}
                fallbackSrc={image.fallbackSrc || image.src}
                alt=""
                role="img"
                onLoad={finishLoading}
                onError={handleImageError}
            />

            {loading ? (
                <div className={styles.loader}>
                    <RectangleSkeleton />
                </div>
            ) : null}

            {!loading && image.labels
                ? image.labels.map((label, index) => (
                      <p key={`${label.content}-${index}`} style={label.position} className={styles.label}>
                          {label.content}
                      </p>
                  ))
                : null}

            {shouldShowExpandableIcon ? (
                <>
                    <IconButton
                        className={`${styles.controlButton} ${styles.expandButton}`}
                        onClick={() => {
                            setExpandedIndex(imageIndex);
                            setExpandedImage(image.src);
                            setExpandedFallbackImage(image.fallbackSrc || image.src);
                            imageModalRef.current?.openModal();
                        }}
                        data-testid="camera-image-card-expand"
                    >
                        <ExpandIcon color="#ffffff" className={styles.controlIcon} />
                    </IconButton>

                    <ExpandedCameraImageModal
                        modalRef={imageModalRef}
                        imageSrc={expandedImage}
                        fallbackSrc={expandedFallbackImage}
                        onClose={() => {
                            setExpandedImage(null);
                            setExpandedFallbackImage(null);
                        }}
                        onPrev={
                            images.length > 1 && expandedIndex > 0
                                ? () => {
                                      const prevIndex = expandedIndex - 1;
                                      const prev = images[prevIndex];
                                      setExpandedIndex(prevIndex);
                                      setExpandedImage(prev.src);
                                      setExpandedFallbackImage(prev.fallbackSrc || prev.src);
                                  }
                                : null
                        }
                        onNext={
                            images.length > 1 && expandedIndex < images.length - 1
                                ? () => {
                                      const nextIndex = expandedIndex + 1;
                                      const next = images[nextIndex];
                                      setExpandedIndex(nextIndex);
                                      setExpandedImage(next.src);
                                      setExpandedFallbackImage(next.fallbackSrc || next.src);
                                  }
                                : null
                        }
                    />
                </>
            ) : null}
        </div>
    );
}
